import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Lock,
  Shield,
  Bell,
  CreditCard,
  Globe,
  Briefcase,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');`;

const T = {
  bg: "#ffffff",
  surface: "#fafafa",
  border: "#e5e5e5",
  border2: "#d4d4d4",
  text: "#0a0a0a",
  textSoft: "#525252",
  muted: "#a3a3a3",
  accent: "#0a0a0a",
  serif: "'Cormorant Garamond', serif",
  sans: "'Montserrat', sans-serif",
};

const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const id = userId || "guest";

  const settings = [
    { label: "Personal Information", sub: "Name, email & profile details", icon: <User size={18} />, path: "/editProfile" },
    { label: "Login & Security", sub: "Password & authentication", icon: <Lock size={18} />, path: "/loginsecurity" },
    { label: "Privacy", sub: "Data sharing & visibility", icon: <Shield size={18} />, path: "/privacy" },
    { label: "Languages & Currency", sub: "Region & display preferences", icon: <Globe size={18} />, path: "/CurrencySelection" },
  ];

  return (
    <div style={S.root}>
      <style>{FONT_CSS}</style>

      {/* ── Header ── */}
      <header style={S.header}>
        <div style={S.headerLeft} onClick={() => navigate(-1)}>
          <ChevronLeft size={20} color="#e8e8e8" />
          <span style={S.backLabel}>Back</span>
        </div>
        <span style={S.headerBrand}>COSMOOLA</span>
        <div style={{ width: 60 }} />
      </header>

      {/* ── Content ── */}
      <div style={S.content}>
        <div style={S.titleBlock}>
          <h1 style={S.pageTitle}>Account Settings</h1>
          <p style={S.pageSub}>Manage your personal information, security, and preferences.</p>
        </div>

        <div style={S.list}>
          {settings.map((item, index) => (
            <motion.button
              key={index}
              style={S.card}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              whileHover={{ x: 4 } as any}
              whileTap={{ scale: 0.98 } as any}
              onClick={() => navigate(`${item.path}/${id}`)}
            >
              <div style={S.iconWrap}>{item.icon}</div>
              <div style={S.cardText}>
                <div style={S.cardLabel}>{item.label}</div>
                <div style={S.cardSub}>{item.sub}</div>
              </div>
              <ChevronRight size={16} color={T.muted} />
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;

const S: { [key: string]: React.CSSProperties } = {
  root: {
    minHeight: "100vh",
    background: T.bg,
    fontFamily: T.sans,
  },

  /* Header — dark */
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "rgba(0,0,0,0.95)",
    backdropFilter: "blur(12px)",
    borderBottom: "1px solid #1c1c1c",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 20px",
    height: 56,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  },
  backLabel: {
    color: "#e8e8e8",
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: "0.05em",
    fontFamily: T.sans,
  },
  headerBrand: {
    position: "absolute" as const,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#ffffff",
    fontWeight: 300,
    letterSpacing: "0.3em",
    fontSize: 14,
    fontFamily: T.serif,
  },

  /* Content */
  content: {
    maxWidth: 580,
    margin: "0 auto",
    padding: "32px 20px 80px",
  },

  /* Title */
  titleBlock: {
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 300,
    color: T.text,
    fontFamily: T.serif,
    letterSpacing: "0.04em",
    margin: 0,
    marginBottom: 6,
  },
  pageSub: {
    fontSize: 13,
    color: T.textSoft,
    margin: 0,
    lineHeight: 1.5,
  },

  /* List */
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 0,
  },

  /* Card */
  card: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: "none",
    border: "none",
    borderBottom: "1px solid " + T.border,
    borderRadius: 0,
    padding: "20px 4px",
    cursor: "pointer",
    width: "100%",
    textAlign: "left" as const,
    fontFamily: "inherit",
    transition: "background 0.15s",
  },

  iconWrap: {
    width: 42,
    height: 42,
    borderRadius: 0,
    background: "rgba(0,0,0,0.03)",
    border: "1px solid " + T.border,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: T.text,
  },

  cardText: {
    flex: 1,
  },
  cardLabel: {
    fontSize: 15,
    fontWeight: 500,
    color: T.text,
    letterSpacing: "0.01em",
  },
  cardSub: {
    fontSize: 11,
    color: T.muted,
    marginTop: 2,
  },
};